import { reactive, computed, onMounted } from 'vue'
import { useStoreHooks } from '@/store/hook'
import { useI18n } from 'vue-i18n'
import { submitAnswer, getExamQualify, QUESTION_TYPES } from '@/request/api/exam'
import { useRoute } from 'vue-router'
import { useCountdown, useModalConfirmHook } from '@/utils/hooks'

export const CONTENT_TYPE = {
  exam: 'exam',
  analysis: 'analysis'
}

// 实操考试类型
export const PRACTICAL_EXAM_TYPE = {
  exercise: '2',
  exam: '1'
}

export const QUESTION_TYPES_MAP = {
  [QUESTION_TYPES.single]: 'exam.question.pickOne',
  [QUESTION_TYPES.multi]: 'exam.question.multiple',
  [QUESTION_TYPES.judge]: 'exam.question.trueOrFalse'
}

// 考试状态
export const EXAM_PASS_STATUS = {
  fail: '0',
  pass: '1',
  notParticipate: '2'
}

// 考试类型
export const EXAM_TYPE = {
  topic: '2',
  quiz: '1',
  theoryExam: '0'
}

// 是否有实操考试
export const HAS_PRACTOCAL_EXAM = {
  yes: '1',
  no: '0'
}

// 用户是否有资格参加考试
export const USER_QUALIFIED_STATUS = {
  yes: '1',
  no: '0'
}

export const QUSTION_NO = [
  'A', 'B', 'C', 'D', 'E', 'F', 'G',
  'H', 'I', 'J', 'K', 'L', 'M', 'N',
  'O', 'P', 'Q', 'R', 'S', 'T', 'U',
  'V', 'W', 'X', 'Y', 'Z'
]

// 获取考试初始化信息
export const useExamInitialHook = (isMobile?: boolean) => {
  const { commit, dispatch } = useStoreHooks()
  const route = useRoute()
  const { confirm } = useModalConfirmHook()
  const { t } = useI18n()
  // const router = useRouter()
  const initExamInfo = () => {
    onMounted(async () => {
      commit('exam/initExamStoreData')
      const { relationId, relationType, courseType } = route.query as Record<string, string>
      /**
       * 1. 查询用户是否有资格参加exam/quiz, 没有的话弹出弹框提示
       * 2. 有资格的情况下，查询考试状态
      */
      const res = await getExamQualify({
        releaseId: relationId, relationType
      })
      const status = (res?.data as { entryStatus: string })?.entryStatus || USER_QUALIFIED_STATUS.no
      commit('exam/setEntryStatus', status)
      if (status === USER_QUALIFIED_STATUS.yes) {
        dispatch('exam/queryExamStatus', {
          releaseId: relationId,
          relationType
        })
      } else {
        // 弹窗
        confirm({
          content: t('exam.userUnqualify'),
          hideCancelButton: true,
          okText: t('exam.back'),
          closable: false,
          keyboard: false,
          onOk () {
            let url = relationType === EXAM_TYPE.quiz ? `/course/detail/${relationId}?courseType=${courseType}`
              : relationType === EXAM_TYPE.theoryExam ? `/certification/detail/${relationId}`
                : `/topic/detail/${relationId}`
            // router.push(url)
            if (isMobile) {
              url = `/${window.prefix}/mobile` + url
            } else {
              url = `/${window.prefix}` + url
            }
            // 兼容移动端跳转
            location.href = url
          }
        })
      }
    })
  }
  const getExamType = () => {
    const { relationType } = route.query
    const examType: Record<string, string> = {
      0: 'exam',
      1: 'quiz'
    }
    return examType[relationType as string]
  }
  return {
    initExamInfo,
    getExamType
  }
}

// 获取考试相关状态
export const useExamStatusHook = () => {
  const { state: _state } = useStoreHooks()
  const isArSite = computed(() => _state.global.isArSite)
  const route = useRoute()
  const ifInExam = computed(() => _state.exam.ifUserInExam)
  const examResult = computed(() => _state.exam.examResult)
  const userQualified = computed(() => _state.exam.entryStatus === USER_QUALIFIED_STATUS.yes && examResult.value.ifStatusChecked)
  // 判断用户是否在考试中
  const checkUserInExam = () => {
    return location.pathname.endsWith('/exam') && ifInExam.value
  }
  const type = (route.query as Record<string, string>).relationType
  /**
   * 用户是否可以开始考试
   * 1. 理论考试：用户有资格参加考试 && 用户考试次数没有超过2 && 用户passStatus不为pass
   * 2. 课堂测验：用户有资格参加考试即可
   * 3. 如果isInExam为false，则直接返回false
  */
  const shouldUserStartExam = computed(() => {
    if (!ifInExam.value || !examResult.value.ifStatusChecked) {
      return false
    }
    if (type === EXAM_TYPE.quiz || type === EXAM_TYPE.topic) {
      return userQualified.value
    } else {
      // 理论考试要判断次数
      return userQualified.value && Number(examResult.value.examNums || 0) < 2 && examResult.value.passStatus !== EXAM_PASS_STATUS.pass
    }
  })
  return {
    checkUserInExam,
    examResult,
    type,
    shouldUserStartExam,
    userQualified,
    isArSite
  }
}

// 获取考试内容相关信息
export const useExamHook = () => {
  const { t } = useI18n()
  const route = useRoute()
  const { relationId, relationType, courseType = '0' } = route.query as Record<string, string>
  const { state: _state, dispatch, commit } = useStoreHooks()
  const { confirm } = useModalConfirmHook()
  const state = reactive({
    currentPage: 1,
    loading: false
  })
  const { countdown, hours, minutes, seconds } = useCountdown()
  const examResult = computed(() => _state.exam.examResult)
  // 考试信息
  const examInfo = computed(() => _state.exam.examInfo)
  // 问题列表
  const questionList = computed(() => _state.exam.examInfo.questionHisList || [])
  // 当前题目
  const currentQues = computed(() => questionList.value[state.currentPage - 1] || {})
  // 问题解析列表
  const questionHisCheckList = computed(() => _state.exam.examInfo.questionHisCheckList || [])
  // 当前题目解析
  const currentQuesAnalysis = computed(() => questionHisCheckList.value[state.currentPage - 1] || {})
  // 当前题目-选项
  const currentOptions = computed(() => currentQues.value?.hisOptionList || [])
  // 页码-考试中
  const questionNumberList = computed(() => questionList.value.map((item, index) => (index + 1)))
  // 页码-答案解析
  const analysisNumberList = computed(() => {
    return questionHisCheckList.value.map((item, index) => {
      return {
        ...item,
        pageNo: index + 1
      }
    }).filter(item => item.answerPass !== '1').map(i => i.pageNo)
  })

  const isRadioQues = (type: number) => {
    return type === QUESTION_TYPES.single || type === QUESTION_TYPES.judge
  }

  const checkIfQuestionAnswered = (ques: { checkedId: string | string[] | null }) => {
    if (typeof ques?.checkedId === 'string' || !ques?.checkedId) {
      return !!ques?.checkedId
    }
    return !!ques?.checkedId.length
  }

  // 获取考试结果
  const queryExamStatus = async (theoryExamScore: number, examRecordsId: string) => {
    await dispatch('exam/queryExamStatus', {
      releaseId: relationId,
      relationType,
      score: theoryExamScore,
      examRecordsId,
      courseType
    })
    commit('exam/setIfUserInExam', false)
  }

  // 发起提交试卷请求
  const submitAnswers = () => {
    const answerList = questionList.value.map(i => ({
      questionHisId: i.hisOptionList[0]?.questionHisRefId || '',
      questionType: i.questionType,
      answer: Array.isArray(i.checkedId) ? JSON.stringify(i.checkedId) : i.checkedId as string | string[]
    }))
    state.loading = true
    submitAnswer({
      examRefId: examInfo.value.examId,
      paperHisRefId: questionList.value[0]?.paperHisRefId,
      answerList,
      releaseId: relationId,
      relationType
    }).then(async (res) => {
      const data = res.data as {
        theoryExamScore: number
        examRecordsId: string
      }
      await queryExamStatus(data.theoryExamScore, data.examRecordsId)
      state.loading = false
    }).catch(() => {
      state.loading = false
    })
  }

  // 用户提交试卷
  const onSubmit = () => {
    // 检查用户是否回答了所有问题
    const hasUnansweredQues = questionList.value.some(i => !checkIfQuestionAnswered(i))
    const unansweredModalProps = {
      content: t('exam.submitTipOnUnanswered'),
      okText: t('exam.goBack'),
      cancelText: t('exam.confirmSubmit'),
      closable: false,
      onCancel () {
        submitAnswers()
      }
    }
    const modalProps = {
      content: t('exam.submitTip'),
      onOk () {
        submitAnswers()
      }
    }
    const props = hasUnansweredQues ? unansweredModalProps : modalProps
    confirm({
      ...props
    })
  }

  // 开始考试-获取试卷内容
  const startExam = async () => {
    state.loading = true
    await dispatch('exam/getExamInfo', {
      relationType,
      releaseId: relationId
    })
    countdown(Date.now() + examInfo.value.countDown * 1000, onTimeout)
    state.loading = false
  }

  // 获取答题解析
  const getAnalysis = async () => {
    state.loading = true
    await dispatch('exam/getExamAnalysis', {
      examRefId: examInfo.value.examId,
      examRecordsId: examResult.value.examRecordsId
    })
    state.currentPage = analysisNumberList.value[0]
    state.loading = false
  }

  const goNext = (type: string) => {
    if (type === CONTENT_TYPE.exam) {
      if (state.currentPage < questionList.value.length) {
        state.currentPage += 1
      }
    } else {
      const index = analysisNumberList.value.findIndex(item => item === state.currentPage)
      state.currentPage = analysisNumberList.value[index + 1]
    }
  }
  const goPrevious = (type: string) => {
    if (type === CONTENT_TYPE.exam) {
      if (state.currentPage > 1) {
        state.currentPage = state.currentPage - 1
      }
    } else {
      const index = analysisNumberList.value.findIndex(item => item === state.currentPage)
      state.currentPage = analysisNumberList.value[index - 1]
    }
  }
  const onChangePage = (page: number) => {
    state.currentPage = page
  }

  const onTimeout = () => {
    confirm({
      content: t('exam.timeoutTip'),
      closable: false,
      hideCancelButton: true,
      okText: t('exam.result.back'),
      onOk () {
        submitAnswers()
      }
    })
  }

  return {
    state,
    examInfo,
    questionList,
    currentOptions,
    currentQues,
    isRadioQues,
    onSubmit,
    goNext,
    goPrevious,
    onChangePage,
    hours,
    minutes,
    seconds,
    checkIfQuestionAnswered,
    startExam,
    getAnalysis,
    currentQuesAnalysis,
    questionNumberList,
    analysisNumberList
  }
}

export const useExamAnalysisHook = () => {
  const analysisState = reactive({
    visible: false
  })
  const { state: _state } = useStoreHooks()
  const analysisButtonVisible = computed(() => _state.exam.examInfo.viewAnswerDetails === 1)
  const correctAnswerVisible = computed(() => _state.exam.examInfo.viewAnswer === 1)
  const correctAnswerAnalysisVisible = computed(() => correctAnswerVisible.value && _state.exam.examInfo.viewPaperAnalusis === 1)
  const onViewAnalysis = () => {
    analysisState.visible = true
  }
  const onHideAnalysis = () => {
    analysisState.visible = false
  }
  return {
    analysisState,
    onHideAnalysis,
    onViewAnalysis,
    analysisButtonVisible,
    correctAnswerVisible,
    correctAnswerAnalysisVisible
  }
}
